import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import React, { Suspense } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls/Button/Button";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner/InfoBanner";
import { StatusInformationOutlineIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  VStack,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import {
  hydrateUnknownGuestPlayerCountsAtom,
  reconcileSignUpsWithUnknownGuestCountsAtom,
} from "../../atoms/actions";
import {
  currentEventSignUpStepAtom,
  currentSignUpRowAtom,
  eventSignUpProcessDispatchAtom,
  isCurrentStepValidAtom,
  isFirstStepAtom,
  isLastStepAtom,
  isCurrentSignUpOnWaitlistAtom,
  notifyMembersAtom,
} from "../../atoms/eventSignUpProcess";
import {
  isUnknownGuestCountValidAtom,
  remainingUnpaidGuestCountsAtom,
  unknownGuestsPerPrimaryChargeTypeAtom,
} from "../../atoms/selectors";
import { useGetEventSignUp } from "../../hooks/useGetEventSignUp";
import { useGetPrimaryChargeNameAndPrice } from "../../hooks/useGetPrimaryChargeNameAndPrice";
import { EventSignUpRow } from "../../types";
import { EventDetailCancelWindow } from "../EventDetailCancelWindow";
import { ActionButtonsEventSignUp_event$key } from "./__generated__/ActionButtonsEventSignUp_event.graphql";
import { WaitlistWarningBanner } from "./components/WaitlistWarningBanner/WaitlistWarningBanner";

type ActionButtonsEventSignUpProps = {
  eventRef: ActionButtonsEventSignUp_event$key;
  cancelCallback: () => void;
  onSaveCallback: (signUpRow: EventSignUpRow, notifyMembers?: boolean) => void;
  signUpRow: EventSignUpRow;
  saveIsDisabled?: boolean;
};

export const ActionButtonsEventSignUp = ({
  eventRef,
  cancelCallback,
  onSaveCallback,
  signUpRow,
  saveIsDisabled,
}: ActionButtonsEventSignUpProps) => {
  const dispatchEventSignUpActions = useSetAtom(eventSignUpProcessDispatchAtom);
  const currentStep = useAtomValue(currentEventSignUpStepAtom);
  const isFirstStep = useAtomValue(isFirstStepAtom);
  const isLastStep = useAtomValue(isLastStepAtom);
  const isCurrentSignUpOnWaitlist = useAtomValue(isCurrentSignUpOnWaitlistAtom);
  const isCurrentStepValid = useAtomValue(isCurrentStepValidAtom);
  const currentSignUpRow = useAtomValue(currentSignUpRowAtom);
  const isUnknownGuestCountValid = useAtomValue(isUnknownGuestCountValidAtom);

  const updateUnknownGuestCounts = useSetAtom(
    hydrateUnknownGuestPlayerCountsAtom
  );

  const updateAndGetReconciledUnknownGuestSignUps = useSetAtom(
    reconcileSignUpsWithUnknownGuestCountsAtom
  );

  const unknownGuestTotals = useAtomValue(
    unknownGuestsPerPrimaryChargeTypeAtom
  );
  const remainingUnknownGuestCounts = useAtomValue(
    remainingUnpaidGuestCountsAtom
  );
  const isInErrorState = currentStep === null;

  const shouldHideActionButtons = isInErrorState;

  const event = useFragment(ActionButtonsEventSignUpFragmentString, eventRef);
  const primaryChargeNamesAndPrices = useGetPrimaryChargeNameAndPrice({
    eventRef: event,
  });
  const eventSignUpQueryRef = useGetEventSignUp(signUpRow.id || "");
  const notifyMembers = useAtomValue(notifyMembersAtom);
  const handleNextClick = () => {
    //After each step we re-build our map of unknown guest signs up counts,
    //unless it's the step where we are selecting who pays -- in which case we respect
    //those counts
    if (currentStep !== "PRIMARY_CHARGE_WHO_PAYS") {
      const signUpRow = currentSignUpRow
        ? currentSignUpRow
        : ({} as EventSignUpRow);
      updateUnknownGuestCounts({ signUpRow: signUpRow });
    }

    const updatedCurrentSignUpRow =
      currentStep === "PRIMARY_CHARGE_WHO_PAYS"
        ? updateAndGetReconciledUnknownGuestSignUps()
        : currentSignUpRow;

    //@TODO: fix this -- it'll just run off the end it should error
    isLastStep && updatedCurrentSignUpRow
      ? onSaveCallback(updatedCurrentSignUpRow, notifyMembers)
      : dispatchEventSignUpActions({ type: "nextStep" });
  };

  const areUnknownGuestsInvalid =
    currentStep === "PRIMARY_CHARGE_WHO_PAYS" && !isUnknownGuestCountValid;

  const isNextButtonDisabled =
    !isCurrentStepValid || areUnknownGuestsInvalid || saveIsDisabled;
  const finalButtonLabel = isCurrentSignUpOnWaitlist
    ? t`Confirm`
    : t`Save Changes`;

  return shouldHideActionButtons ? null : (
    <Box>
      {eventSignUpQueryRef ? (
        <Suspense fallback={null}>
          <EventDetailCancelWindow eventSignUpQueryRef={eventSignUpQueryRef} />
        </Suspense>
      ) : null}

      <VStack
        borderColor="grayscale-200"
        borderTopWidth={2}
        backgroundColor="surface-primary"
        paddingHorizontal={6}
      >
        {areUnknownGuestsInvalid
          ? remainingUnknownGuestCounts.map((item) => {
              const chargeName =
                primaryChargeNamesAndPrices.find(
                  (charge) => charge.primaryChargeId === item.primaryChargeId
                )?.chargeName ?? "";
              const total =
                unknownGuestTotals.find(
                  (ugt) => ugt.primaryChargeId === item.primaryChargeId
                )?.total ?? 0;
              const unknownGuestMessage = `Unknown Guest: ${chargeName} allocations must add up to ${total}`;
              return (
                <Box mt={2}>
                  <InfoBanner
                    type="CARD"
                    leadingIcon={StatusInformationOutlineIcon}
                    message={unknownGuestMessage}
                    colorTheme="NEUTRAL"
                  />
                </Box>
              );
            })
          : null}

        <WaitlistWarningBanner />

        <HStack
          alignItems="center"
          justifyContent={"space-between"}
          pt={!isUnknownGuestCountValid ? 4 : 6}
          pb={6}
        >
          <Button
            onPress={() => {
              isFirstStep
                ? cancelCallback()
                : dispatchEventSignUpActions({ type: "previousStep" });
            }}
            variant={"secondary"}
            accessibilityLabel={
              isFirstStep ? t`Cancel Button` : t`Previous Button`
            }
          >
            {isFirstStep ? t`Cancel` : t`Previous`}
          </Button>
          <Button
            testID="next-button"
            isDisabled={isNextButtonDisabled}
            onPress={handleNextClick}
            accessibilityLabel={
              isLastStep && isCurrentSignUpOnWaitlist
                ? t`Confirm Button`
                : isLastStep
                ? t`Save Button`
                : t`Next Button`
            }
          >
            {isLastStep ? finalButtonLabel : t`Next`}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

const ActionButtonsEventSignUpFragmentString = graphql`
  fragment ActionButtonsEventSignUp_event on Event {
    ...useGetPrimaryChargeNameAndPrice_event
    allowedGroupSizes
    signUpWindows {
      edges {
        node {
          cancelDeadlineAt
          cancelDeadlineDaysAhead
          cancelDeadlineTime
        }
      }
    }
  }
`;
